import { isDemoMode } from "@/lib/runtimeMode";
import { getSupabaseClient } from "../providers/supabase/supabase";
export type PushPreferences = {
  assigned: boolean;
  status: boolean;
  comments: boolean;
  due: boolean;
};
export const defaultPushPreferences: PushPreferences = {
  assigned: true,
  status: true,
  comments: true,
  due: true,
};
export async function pushRequest(body: Record<string, unknown>) {
  if (isDemoMode() || !import.meta.env.VITE_SUPABASE_URL)
    throw new Error(
      "Push будет доступен после подключения сервера уведомлений. В учебной базе отправка отключена.",
    );
  const { data, error } = await getSupabaseClient().functions.invoke("push", {
    body,
  });
  if (error || data?.error)
    throw new Error(
      data?.error ?? "Не удалось связаться с сервером уведомлений",
    );
  return data;
}
export async function detachPush() {
  if (isDemoMode()) return;
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;
  const registration = await navigator.serviceWorker.getRegistration();
  const subscription = await registration?.pushManager.getSubscription();
  if (!subscription) return;
  const removal = pushRequest({
    action: "unsubscribe",
    endpoint: subscription.endpoint,
  }).catch(() => {});
  await subscription.unsubscribe();
  await Promise.race([
    removal,
    new Promise((resolve) => window.setTimeout(resolve, 2500)),
  ]);
}
export function applicationKey(value: string): ArrayBuffer {
  const bytes = Uint8Array.from(
    atob(value.replace(/-/g, "+").replace(/_/g, "/")),
    (char) => char.charCodeAt(0),
  );
  return bytes.buffer;
}

export function supportsPushNotifications() {
  return (
    "Notification" in window &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    window.isSecureContext
  );
}
export async function readyPushRegistration() {
  const registration = await navigator.serviceWorker.getRegistration();
  if (registration?.active) return registration;
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    const ready = await Promise.race([
      navigator.serviceWorker.ready,
      new Promise<never>((_, reject) => {
        timer = setTimeout(
          () =>
            reject(
              new Error(
                "Служба уведомлений ещё запускается. Повторите проверку через несколько секунд.",
              ),
            ),
          8000,
        );
      }),
    ]);
    if (!ready?.active)
      throw new Error("Служба уведомлений ещё не готова. Повторите проверку.");
    return ready;
  } finally {
    clearTimeout(timer);
  }
}

export async function readPushState(): Promise<{
  subscribed: boolean;
  preferences: PushPreferences;
}> {
  if (
    isDemoMode() ||
    !supportsPushNotifications() ||
    Notification.permission !== "granted"
  )
    return { subscribed: false, preferences: defaultPushPreferences };
  const registration = await readyPushRegistration();
  const subscription = await registration.pushManager.getSubscription();
  if (!subscription)
    return { subscribed: false, preferences: defaultPushPreferences };
  const status = await pushRequest({
    action: "status",
    endpoint: subscription.endpoint,
  });
  if (typeof status.subscribed !== "boolean")
    throw new Error(
      "Не удалось проверить состояние уведомлений. Повторите проверку.",
    );
  return {
    subscribed: status.subscribed,
    preferences: { ...defaultPushPreferences, ...status.preferences },
  };
}
export async function hasEnabledPush(): Promise<boolean | null> {
  return (await readPushState()).subscribed;
}
export async function enablePushNotifications(
  preferences = defaultPushPreferences,
) {
  if (isDemoMode())
    throw new Error(
      "В учебной базе push не отправляются. Их можно будет включить в рабочей CRM.",
    );
  if (!supportsPushNotifications())
    throw new Error(
      "В этом браузере push недоступен. На iPhone добавьте CRM на экран «Домой» и откройте её через значок приложения.",
    );
  if (Notification.permission === "denied")
    throw new Error(
      "Уведомления заблокированы. Разрешите их в настройках сайта в браузере, затем нажмите «Включить» ещё раз.",
    );
  const permission =
    Notification.permission === "granted"
      ? "granted"
      : await Notification.requestPermission();
  if (permission !== "granted")
    throw new Error(
      "Разрешите уведомления для CRM в настройках браузера, затем повторите включение.",
    );
  const { publicKey } = await pushRequest({ action: "config" });
  if (!publicKey) throw new Error("Сервер push-уведомлений ещё не настроен.");
  const registration = await readyPushRegistration();
  const subscription =
    (await registration.pushManager.getSubscription()) ??
    (await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: applicationKey(publicKey),
    }));
  await pushRequest({
    action: "subscribe",
    subscription: subscription.toJSON(),
    preferences,
  });
}
